<template>
    <div>
        <v-row>
            <v-col cols="12" sm="4">
                <v-card
                    outlined
                    :class="{ 'active-filter-card': activeFilter === 'Pending' }"
                    style="cursor:pointer"
                    @click="filterByStatus('Pending')"
                >
                    <v-card-text>
                        <div class="d-flex align-center mb-2">
                            <v-icon color="orange darken-2" left>mdi-clock-outline</v-icon>
                            <span class="subtitle-2 orange--text text--darken-2">{{ $t('kelaBatch.summaryPending') }}</span>
                            <v-spacer></v-spacer>
                            <v-icon v-if="activeFilter === 'Pending'" small color="primary">mdi-filter</v-icon>
                        </div>
                        <template v-if="summaryLoading">
                            <v-skeleton-loader type="text" width="120" />
                            <v-skeleton-loader type="text" width="160" class="mt-1" />
                        </template>
                        <template v-else-if="summary">
                            <div class="text-h5 font-weight-bold">{{ summary.pendingFeedback.batchCount }} {{ $t('kelaBatch.batches') }}</div>
                            <div class="body-2 grey--text">{{ summary.pendingFeedback.transportEventCount }} {{ $t('kelaBatch.events') }}</div>
                            <div class="body-1 mt-1">{{ formatCurrency(summary.pendingFeedback.totalReimbursement) }}</div>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="4">
                <v-card
                    outlined
                    :class="{ 'active-filter-card': activeFilter === 'OK' }"
                    style="cursor:pointer"
                    @click="filterByStatus('OK')"
                >
                    <v-card-text>
                        <div class="d-flex align-center mb-2">
                            <v-icon color="green darken-2" left>mdi-check-circle-outline</v-icon>
                            <span class="subtitle-2 green--text text--darken-2">{{ $t('kelaBatch.summaryProcessed') }}</span>
                            <v-spacer></v-spacer>
                            <v-icon v-if="activeFilter === 'OK'" small color="primary">mdi-filter</v-icon>
                        </div>
                        <template v-if="summaryLoading">
                            <v-skeleton-loader type="text" width="120" />
                            <v-skeleton-loader type="text" width="160" class="mt-1" />
                        </template>
                        <template v-else-if="summary">
                            <div class="text-h5 font-weight-bold">{{ summary.processed.batchCount }} {{ $t('kelaBatch.batches') }}</div>
                            <div class="body-2 grey--text">{{ summary.processed.transportEventCount }} {{ $t('kelaBatch.events') }}</div>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" sm="4">
                <v-card
                    outlined
                    :class="{ 'active-filter-card': activeFilter === 'Error' }"
                    style="cursor:pointer"
                    @click="filterByStatus('Error')"
                >
                    <v-card-text>
                        <div class="d-flex align-center mb-2">
                            <v-icon color="red darken-2" left>mdi-alert-circle-outline</v-icon>
                            <span class="subtitle-2 red--text text--darken-2">{{ $t('kelaBatch.summaryError') }}</span>
                            <v-spacer></v-spacer>
                            <v-icon v-if="activeFilter === 'Error'" small color="primary">mdi-filter</v-icon>
                        </div>
                        <template v-if="summaryLoading">
                            <v-skeleton-loader type="text" width="120" />
                            <v-skeleton-loader type="text" width="160" class="mt-1" />
                        </template>
                        <template v-else-if="summary">
                            <div class="text-h5 font-weight-bold">{{ summary.error.batchCount }} {{ $t('kelaBatch.batches') }}</div>
                            <div class="body-2 grey--text">{{ summary.error.transportEventCount }} {{ $t('kelaBatch.events') }}</div>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12">
                <v-card outlined>
                    <v-card-title class="subtitle-1">{{ $t('kelaBatch.monthlyChart') }}</v-card-title>
                    <v-card-text>
                        <v-skeleton-loader v-if="summaryLoading" type="image" />
                        <bar-chart
                            v-else-if="summary && summary.monthlyProcessed && summary.monthlyProcessed.length > 0"
                            :chart-data="barChartData"
                            :options="barChartOptions"
                            :style="{ height: '280px' }"
                        />
                        <div v-else class="grey--text text-center py-8">-</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import BarChart from "@/components/Charts/BarChart";

const REGION_COLORS = {
    KAB: "#1B4332",
    KHC: "#2D6A4F",
    KPB: "#40916C",
    KLC: "#52B788",
    LAC: "#74C69D",
    PHB: "#95D5B2",
    UMB: "#B7E4C7"
};

export default {
    name: "KelaBatchSummary",
    components: { BarChart },
    computed: {
        ...mapGetters("kelaBatch", ["summary", "summaryLoading", "searchParams"]),
        activeFilter() {
            return (this.searchParams && this.searchParams.FeedbackStatus) || null;
        },
        barChartData() {
            if (!this.summary || !this.summary.monthlyProcessed) return null;

            const rows = this.summary.monthlyProcessed;

            const monthKeys = [
                ...new Set(rows.map(r => `${String(r.month).padStart(2, "0")}/${r.year}`))
            ].sort((a, b) => {
                const [am, ay] = a.split("/");
                const [bm, by] = b.split("/");
                return by !== ay ? by - ay : bm - am;
            });

            const regions = [...new Set(rows.map(r => r.region))].sort();

            const datasets = regions.map(region => ({
                label: region,
                backgroundColor: REGION_COLORS[region] || "#888888",
                data: monthKeys.map(mk => {
                    const [m, y] = mk.split("/");
                    const row = rows.find(
                        r =>
                            r.region === region &&
                            r.month === parseInt(m) &&
                            r.year === parseInt(y)
                    );
                    return row ? row.totalReimbursement : 0;
                })
            }));

            return { labels: monthKeys, datasets };
        },
        barChartOptions() {
            return {
                maintainAspectRatio: false,
                scales: {
                    xAxes: [{ stacked: true }],
                    yAxes: [
                        {
                            stacked: true,
                            ticks: {
                                callback: value =>
                                    value.toLocaleString("fi-FI", {
                                        style: "currency",
                                        currency: "EUR",
                                        maximumFractionDigits: 0
                                    })
                            }
                        }
                    ]
                },
                tooltips: {
                    callbacks: {
                        label: (tooltipItem, data) => {
                            const label = data.datasets[tooltipItem.datasetIndex].label;
                            const value = tooltipItem.yLabel;
                            return `${label}: ${value.toLocaleString("fi-FI", {
                                style: "currency",
                                currency: "EUR"
                            })}`;
                        }
                    }
                }
            };
        }
    },
    methods: {
        filterByStatus(status) {
            const next = this.activeFilter === status ? null : status;
            this.$emit("filter-by-status", next);
        },
        formatCurrency(value) {
            if (value == null) return "-";
            return value.toLocaleString("fi-FI", { style: "currency", currency: "EUR" });
        }
    }
};
</script>

<style scoped>
.active-filter-card {
    border-color: #1976d2 !important;
    border-width: 2px !important;
}
</style>
